import { useEffect, useState, type FormEvent } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../lib/firebase";
import { api, callableErrorMessage } from "../lib/api";

type Props = {
  tenantId: string;
  defaultLegalRut?: string;
  defaultFantasyName?: string;
};

export function MercadoPagoConfigEditor({ tenantId, defaultLegalRut = "", defaultFantasyName = "" }: Props) {
  const [accessToken, setAccessToken] = useState("");
  const [webhookSecret, setWebhookSecret] = useState("");
  const [publicKey, setPublicKey] = useState("");
  const [legalRut, setLegalRut] = useState(defaultLegalRut);
  const [fantasyName, setFantasyName] = useState(defaultFantasyName);
  const [sandbox, setSandbox] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);

  useEffect(() => {
    return onSnapshot(
      doc(db, "tenants", tenantId, "config", "mercadopago"),
      (snap) => {
        const d = snap.data();
        if (d) {
          setEnabled(!!d.accessToken);
          setLegalRut((d.legalRut as string) ?? defaultLegalRut);
          setFantasyName((d.fantasyName as string) ?? defaultFantasyName);
          setSandbox(!!d.sandbox);
        }
      },
      () => {}
    );
  }, [tenantId, defaultLegalRut, defaultFantasyName]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setFeedback(null);
    try {
      const payload: Parameters<typeof api.updateMercadoPagoConfig>[0] = {
        tenantId,
        legalRut: legalRut.trim() || undefined,
        fantasyName: fantasyName.trim() || undefined,
        sandbox
      };
      if (accessToken.trim()) payload.accessToken = accessToken.trim();
      if (webhookSecret.trim()) payload.webhookSecret = webhookSecret.trim();
      if (publicKey.trim()) payload.publicKey = publicKey.trim();

      const res = await api.updateMercadoPagoConfig(payload);
      setFeedback(`✓ Guardado · MP ${res.data.enabled ? "activo" : "sin token"}`);
      setAccessToken("");
      setWebhookSecret("");
      setPublicKey("");
      setTimeout(() => setFeedback(null), 4000);
    } catch (err) {
      setFeedback(`✗ ${callableErrorMessage(err)}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="flex flex-col gap-4">
      <div className="rounded-2xl bg-leaf-50 p-3 text-xs text-leaf-800 ring-1 ring-leaf-200">
        Estado actual: <strong>{enabled ? "✓ Conectado" : "Sin configurar"}</strong>
        {sandbox && enabled && " · Modo sandbox (test)"}
      </div>

      <label className="flex flex-col gap-1 text-sm">
        <span className="text-leaf-700">Access token</span>
        <input
          type="password"
          value={accessToken}
          onChange={(e) => setAccessToken(e.target.value)}
          placeholder={enabled ? "Deja vacío para mantener el actual" : "APP_USR-... o TEST-..."}
          className="input font-mono text-xs"
          autoComplete="off"
        />
        <span className="text-[11px] text-leaf-600">
          Lo encuentras en panel.mercadopago.cl → Tus integraciones → Credenciales de producción.
        </span>
      </label>

      <label className="flex flex-col gap-1 text-sm">
        <span className="text-leaf-700">Webhook secret (opcional, recomendado)</span>
        <input
          type="password"
          value={webhookSecret}
          onChange={(e) => setWebhookSecret(e.target.value)}
          placeholder={enabled ? "Deja vacío para mantener el actual" : "Clave secreta para validar webhooks"}
          className="input font-mono text-xs"
          autoComplete="off"
        />
        <span className="text-[11px] text-leaf-600">
          Lo configuras en panel MP → Webhooks → Configurar notificaciones. Cuando lo definas, validamos signature HMAC.
        </span>
      </label>

      <label className="flex flex-col gap-1 text-sm">
        <span className="text-leaf-700">Public key (opcional, para checkout transparente)</span>
        <input
          value={publicKey}
          onChange={(e) => setPublicKey(e.target.value)}
          placeholder={enabled ? "Deja vacío para mantener el actual" : "APP_USR-..."}
          className="input font-mono text-xs"
          autoComplete="off"
        />
      </label>

      <label className="flex flex-col gap-1 text-sm">
        <span className="text-leaf-700">RUT empresa (para boletas/facturas)</span>
        <input
          value={legalRut}
          onChange={(e) => setLegalRut(e.target.value)}
          placeholder="77.411.698-2"
          className="input"
        />
      </label>

      <label className="flex flex-col gap-1 text-sm">
        <span className="text-leaf-700">Nombre fantasy en estado de cuenta (máx 13 chars)</span>
        <input
          value={fantasyName}
          onChange={(e) => setFantasyName(e.target.value)}
          maxLength={13}
          placeholder="FRESHERB"
          className="input"
        />
      </label>

      <label className="flex items-center gap-2 text-sm text-leaf-800">
        <input
          type="checkbox"
          checked={sandbox}
          onChange={(e) => setSandbox(e.target.checked)}
          className="h-4 w-4 rounded border-leaf-300"
        />
        Modo sandbox (usá el initPoint de prueba)
      </label>

      {feedback && (
        <p
          className={`rounded-xl px-3 py-2 text-xs ${
            feedback.startsWith("✓")
              ? "bg-leaf-50 text-leaf-800"
              : "bg-red-50 text-red-700"
          }`}
        >
          {feedback}
        </p>
      )}

      <button type="submit" disabled={busy} className="btn-primary">
        {busy ? "Guardando…" : "Guardar config Mercado Pago"}
      </button>
    </form>
  );
}
